import axios from 'axios'
import { useAuthStore } from '@store/authStore'
import { toast } from 'react-toastify'

/**
 * Axios instance with interceptors
 */
const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL || '/api',
    timeout: 60000, // 60 seconds for large form updates
    headers: {
        'Content-Type': 'application/json',
    },
})

// Request interceptor - add auth token
api.interceptors.request.use(
    (config) => {
        const token = useAuthStore.getState().token
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
        return config
    },
    (error) => {
        return Promise.reject(error)
    }
)

// Response interceptor - handle errors
api.interceptors.response.use(
    (response) => {
        return response.data
    },
    async (error) => {
        const originalRequest = error.config

        // Handle 401 Unauthorized
        if (error.response?.status === 401) {
            // Only skip refresh/redirect for login and refresh endpoints themselves
            const isLoginOrRefresh = originalRequest.url?.includes('/auth/login') ||
                                     originalRequest.url?.includes('/auth/refresh')

            // Try to refresh token if not already tried and not login/refresh
            if (!originalRequest._retry && !isLoginOrRefresh && useAuthStore.getState().token) {
                originalRequest._retry = true

                try {
                    const response = await api.post('/auth/refresh')
                    const { token } = response.data

                    useAuthStore.getState().updateToken(token)
                    originalRequest.headers.Authorization = `Bearer ${token}`

                    return api(originalRequest)
                } catch (refreshError) {
                    // Refresh failed — clear stale session and redirect to login
                    useAuthStore.getState().logout()
                    window.location.href = '/login'
                    return Promise.reject(refreshError)
                }
            }

            // If auth/me or any protected endpoint returns 401 directly, logout
            if (!isLoginOrRefresh && useAuthStore.getState().isAuthenticated) {
                useAuthStore.getState().logout()
                window.location.href = '/login'
            }
        }

        // Handle 403 Forbidden
        if (error.response?.status === 403) {
            toast.error('Access denied', { toastId: 'access-denied' })
        }

        // Handle 404 Not Found
        if (error.response?.status === 404) {
            toast.error('Resource not found', { toastId: 'not-found' })
        }

        // Handle 422 Validation Error
        if (error.response?.status === 422) {
            const errors = error.response.data.errors
            if (errors) {
                Object.values(errors).forEach((msg, idx) => {
                    toast.error(msg, { toastId: `validation-${idx}` })
                })
            }
        }

        // Handle 500 Server Error
        if (error.response?.status >= 500) {
            toast.error('Server error. Please try again later.', { toastId: 'server-error' })
        }

        // Handle network errors (silently - don't show toast)
        if (!error.response) {
            console.warn('Network error:', error.message)
        }

        return Promise.reject(error)
    }
)

export default api
